// Scalar building blocks shared by every contract (04 §Base rules). Keep these free of transforms
// and defaults: the OpenAPI document is generated from the same schemas in "input" mode.
import { z } from "zod";

/**
 * UUID (the slice issues UUIDv7, but any RFC 9562 UUID parses), canonical lower case once
 * parsed: the one boundary every id crosses, so no comparison downstream (policy rows, fences,
 * in-memory maps, bindings, event payloads) ever meets an upper-case spelling of a stored id
 * (Codex r7). `overwrite` keeps the type and the input-mode JSON schema; it is not a transform.
 */
export const Uuid = z
  .uuid()
  .overwrite((s) => s.toLowerCase())
  .meta({ id: "Uuid" });

const UUID_TEXT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A uuid in canonical lower case (anything else as it is). */
export const canonicalUuid = <T>(v: T): T =>
  (typeof v === "string" && UUID_TEXT.test(v) ? v.toLowerCase() : v) as T;

/** An identifier's key: `id`, `*Id(s)` (MCP arguments), `*_id(s)` (contract payloads). */
const ID_KEY = /^id$|[a-z]Ids?$|_ids?$/;

/**
 * `v` with the uuids held under identifier keys lower-cased, at any depth (an id list too):
 * for values that reach a comparison without crossing `Uuid` (MCP arguments, free-form JSON
 * payloads; Codex r7). Anything else — a title, a description, a field's value — is kept as it
 * was written, even when it looks like a uuid (Codex r8 #2).
 */
export function canonicalIds<T>(v: T, underId = false): T {
  if (typeof v === "string") return underId ? canonicalUuid(v) : v;
  if (Array.isArray(v)) return v.map((x) => canonicalIds(x, underId)) as T;
  if (v && typeof v === "object" && Object.getPrototypeOf(v) === Object.prototype)
    return Object.fromEntries(
      Object.entries(v).map(([k, x]) => [k, canonicalIds(x, ID_KEY.test(k))]),
    ) as T;
  return v;
}

/** ISO-8601 instant with an offset (the API emits `Z`). Mirrors `timestamptz` columns. */
export const Timestamp = z.iso.datetime({ offset: true }).meta({ id: "Timestamp" });

const ISO_INSTANT =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,9}))?(Z|[+-]\d{2}:\d{2})$/i;

/**
 * An ISO instant as epoch microseconds, the precision of `timestamptz`. `Date.parse` keeps
 * milliseconds only, so ordering by it would tie `.000100Z` and `.000900Z` (review #5); every
 * ordering of stored instants goes through this instead. Offsets are normalized.
 */
export function timestampMicros(ts: string): bigint {
  const m = ISO_INSTANT.exec(ts);
  if (!m) throw new Error(`not an ISO instant: ${ts}`);
  const [, y, mo, d, h, mi, s, frac = "", zone = "Z"] = m;
  const ms = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
  const micros = BigInt((frac + "000000").slice(0, 6));
  let offsetMinutes = 0;
  if (zone.toUpperCase() !== "Z") {
    const sign = zone.startsWith("-") ? -1 : 1;
    offsetMinutes = sign * (Number(zone.slice(1, 3)) * 60 + Number(zone.slice(4, 6)));
  }
  return (BigInt(ms) - BigInt(offsetMinutes) * 60_000n) * 1000n + micros;
}

/** Orders two ISO instants at microsecond precision (negative when `a` is earlier). */
export function compareTimestamps(a: string, b: string): number {
  const d = timestampMicros(a) - timestampMicros(b);
  return d < 0n ? -1 : d > 0n ? 1 : 0;
}

/** ISO calendar date `YYYY-MM-DD`. Mirrors `date` columns. */
export const IsoDate = z.iso.date().meta({ id: "IsoDate" });

/**
 * Exact decimal as a string, the JSON form of `numeric` columns (schema decision 14): values
 * never pass through binary floating point on the way out.
 */
export const Decimal = z
  .string()
  .max(20)
  .regex(/^-?\d{1,12}(\.\d{1,6})?$/, "decimal with at most 12 integer and 6 fraction digits");

/** Request-side decimal: a JSON number or a decimal string. */
export const DecimalInput = z.union([z.number().finite(), Decimal]);

/** Safe integer in minor units or micros (`bigint` columns travel as JSON integers). */
export const MinorAmount = z.int();
export const MicrosAmount = z.int();

// Period keys (domain-kpi formats) with calendar ranges, not just shapes (finding #10). Weeks
// run W01–W53 (domain-kpi years have 52 or 53 weeks); days must exist in the calendar.
const PERIOD_PATTERNS = {
  day: /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/,
  week: /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/,
  month: /^\d{4}-(0[1-9]|1[0-2])$/,
  quarter: /^\d{4}-Q[1-4]$/,
  year: /^\d{4}$/,
  round: /^r[1-9]\d*$/,
} as const;
export type PeriodKind = keyof typeof PERIOD_PATTERNS;

function realDay(key: string): boolean {
  const [y, m, d] = key.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/** True when `key` is a valid key of period type `type` (`2026-W42` is a week, not a month). */
export function periodKeyMatches(type: PeriodKind, key: string): boolean {
  return PERIOD_PATTERNS[type].test(key) && (type !== "day" || realDay(key));
}

/** The period type a key has, or null when it is no valid key. */
export function periodKindOf(key: string): PeriodKind | null {
  for (const type of Object.keys(PERIOD_PATTERNS) as PeriodKind[])
    if (periodKeyMatches(type, key)) return type;
  return null;
}

const keyOf = (type: PeriodKind, example: string) =>
  z
    .string()
    .max(16)
    .refine((k) => periodKeyMatches(type, k), `${type} key, e.g. ${example}`);

export const WeekKey = keyOf("week", "2026-W42");
export const MonthKey = keyOf("month", "2026-10");
export const DayKey = keyOf("day", "2026-10-16");

/** Any domain-kpi period key: day, week, month, quarter, year, or an assessment round. */
export const PeriodKey = z
  .string()
  .max(16)
  .refine(
    (k) => periodKindOf(k) !== null,
    "period key (2026-10-16, 2026-W42, 2026-10, 2026-Q4, 2026 or r3)",
  );

/** Refinement for request objects carrying a `period_type` and a key field. */
export function periodPairRule(
  keyField: string,
  typeField = "period_type",
  defaultType?: PeriodKind,
) {
  return (value: Record<string, unknown>, ctx: z.RefinementCtx) => {
    const type = (value[typeField] ?? defaultType) as PeriodKind | undefined;
    const key = value[keyField];
    if (type && typeof key === "string" && !periodKeyMatches(type, key))
      ctx.addIssue({ code: "custom", path: [keyField], message: `not a ${type} key` });
  };
}

/**
 * Refinement for `from`/`to` key ranges: both keys of the same period type, `from` not after
 * `to`. Keys of one type sort lexically in calendar order (zero-padded), rounds numerically.
 */
export function periodRangeRule(value: { from?: unknown; to?: unknown }, ctx: z.RefinementCtx) {
  const { from, to } = value;
  if (typeof from !== "string" || typeof to !== "string") return;
  const kind = periodKindOf(from);
  if (kind !== periodKindOf(to)) {
    ctx.addIssue({
      code: "custom",
      path: ["to"],
      message: "from and to must be the same period type",
    });
    return;
  }
  const after = kind === "round" ? Number(from.slice(1)) > Number(to.slice(1)) : from > to;
  if (after) ctx.addIssue({ code: "custom", path: ["to"], message: "to is before from" });
}

/** Wall-clock time `HH:MM` (24 h). */
export const WallTime = z
  .string()
  .max(5)
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM");

export const Slug = z
  .string()
  .max(63)
  .regex(/^[a-z0-9][a-z0-9-]{1,62}$/, "lowercase letters, digits and dashes, 2–63 chars");

export const Email = z.email().max(254);

/** IANA time zone name, e.g. `America/Chicago`. Validated against the runtime's tz database. */
export const TimeZone = z
  .string()
  .max(64)
  .refine((tz) => {
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  }, "IANA time zone");

/** Dotted lower-case name: event types and ingest item types (`task.completed`). */
export const DottedName = z
  .string()
  .max(64)
  .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/, "dotted lower-case name, e.g. task.completed");

// Request bounds (finding #6): every request string, array and record has a maximum.
export const LIMITS = {
  name: 200,
  text: 2000,
  longText: 10_000,
  key: 64,
  list: 100,
  recordKeys: 50,
  jsonBytes: 16_384,
} as const;

/** Required single-line text, trimmed, 1..`max` chars. */
export const text = (max: number) => z.string().trim().min(1).max(max);

/** A display name or title (1..200 chars). */
export const NonEmpty = text(LIMITS.name);

/** Free text (notes, descriptions), at most `LIMITS.text` chars. */
export const FreeText = z.string().max(LIMITS.text);

/** A record with at most `max` keys (enforced, and published as `maxProperties`). */
export function boundedRecord<V extends z.ZodType>(
  value: V,
  max: number = LIMITS.recordKeys,
  key: z.ZodType<string> = z.string().max(LIMITS.key),
) {
  return z
    .record(key as z.ZodString, value)
    .refine((r) => Object.keys(r).length <= max, `at most ${max} keys`)
    .meta({ maxProperties: max });
}

/** A JSON object with at most `LIMITS.recordKeys` keys and `LIMITS.jsonBytes` serialized bytes. */
export const BoundedJson = boundedRecord(z.unknown()).refine(
  (r) => new TextEncoder().encode(JSON.stringify(r)).length <= LIMITS.jsonBytes,
  `at most ${LIMITS.jsonBytes} bytes as JSON`,
);
